import { StorageError } from '../../app/errors';
import { deleteBlobs } from '../blobStore';
import { requireTransaction } from '../commit';
import { db, read, TABLE } from '../db';
import type { TableName } from '../db';
import { deleteAnnotationsForCascade } from './annotations';
import { deleteCoachCardsForCascade } from './coachCards';
import { deleteHypothesesForCascade } from './hypotheses';
import { deleteMatchesForCascade } from './matches';
import { deleteNotesForCascade } from './matchNotes';
import { deleteObservationsForCascade } from './observations';
import { deleteResolutionsForCascade } from './flagResolutions';
import { deleteSetPiecesForCascade } from './setPieces';
import { deleteTalkPointsForCascade } from './talkPoints';
import { deletePlayersForCascade, deleteTeamsForCascade } from './teams';

/**
 * **INV-8 and BAK-4, together — because they are the same fact seen twice.**
 *
 * INV-8: *"Deleting a Match or Team cascades all dependent rows and audio blobs in one transaction,
 * including TalkPoints and MatchNotes."* Requirements revision 2 added `FlagResolution` to what
 * cascades, and `MatchNote` was already named.
 *
 * BAK-4: *"Deletes cascade per INV-8 behind a confirm step stating the counts of what will be
 * removed."*
 *
 * ── One plan, two uses ───────────────────────────────────────────────────────
 *
 * The counts the user is shown and the rows that are then deleted come from **the same object**.
 * `CascadePlan.counts[t]` is `ids[t].length` by construction, not a second query that could answer
 * differently. A discrepancy there would be a lie to the user at the only moment they can still stop
 * — so the design forecloses it rather than testing for it.
 *
 * ── And the plan is re-checked inside the transaction ────────────────────────
 *
 * A snapshot taken before a modal confirm can go stale: a recorder finalize can land a blob, a
 * background finalize can add a row. Deleting by the *stale* plan would leave an orphan; deleting by
 * a *fresh* plan would remove more than the confirm stated. Neither is acceptable, so `applyCascade`
 * re-plans inside the transaction, compares, and **aborts** if anything changed. The user re-confirms
 * against true counts. The failure mode is an inconvenience; both alternatives are silent.
 */

export type CascadeScope = { kind: 'match'; id: string } | { kind: 'team'; id: string };

/** The tables a cascade may touch. Also the transaction scope, so the two cannot drift apart. */
export const CASCADE_TABLES: readonly TableName[] = [
  TABLE.teams,
  TABLE.players,
  TABLE.matches,
  TABLE.hypotheses,
  TABLE.observations,
  TABLE.flagResolutions,
  TABLE.setPieces,
  TABLE.talkPoints,
  TABLE.matchNotes,
  // B-1 (spec §4.1): the two match-scoped B tables. `ourPlayers` is deliberately absent — the
  // roster has no cascade owner, and the B-1 cascade-negative test asserts a cascade never
  // reaches it.
  TABLE.annotations,
  TABLE.coachCards,
  TABLE.audioBlobs,
];

/** Row ids per table. `audioBlobs` holds blob ids reached through observations and notes. */
export interface CascadeIds {
  teams: string[];
  players: string[];
  matches: string[];
  hypotheses: string[];
  observations: string[];
  flagResolutions: string[];
  setPieces: string[];
  talkPoints: string[];
  matchNotes: string[];
  annotations: string[];
  coachCards: string[];
  audioBlobs: string[];
}

export interface CascadePlan {
  scope: CascadeScope;
  ids: CascadeIds;
  /** Derived from `ids` — never queried separately. This is BAK-4's confirm text. */
  counts: Readonly<Record<keyof CascadeIds, number>>;
  /** Every row and blob that will go, for a one-line "N items" summary. */
  total: number;
}

/**
 * Builds the deletion plan for a scope — BAK-4's confirm input.
 *
 * Read in **one** readonly transaction, so the counts describe one consistent snapshot rather than a
 * series of independently-timed reads that could each see a different moment.
 *
 * @throws {StorageError} if the scope's root row does not exist, or on a read failure. A confirm
 *   dialog for something that is already gone is worse than an error.
 */
export async function planCascade(scope: CascadeScope): Promise<CascadePlan> {
  return read(CASCADE_TABLES, () => collectPlan(scope), 'cascade plan');
}

/**
 * Applies a confirmed plan. INV-8's "in one transaction" is the caller's `write()` scope.
 *
 * @throws {StorageError} outside a transaction; if the database no longer matches the confirmed plan
 *   (`context.staleCascade === true` — re-plan, re-confirm, retry); or on a write failure. Any throw
 *   aborts the transaction, so a partial cascade is not a reachable state.
 */
export async function applyCascade(plan: CascadePlan): Promise<void> {
  requireTransaction('applyCascade');

  const fresh = await collectPlan(plan.scope);
  const drifted = driftedTables(plan.ids, fresh.ids);
  if (drifted.length > 0) {
    throw new StorageError(
      `The data changed since you were shown what would be deleted (${drifted.join(', ')}). Nothing was removed — check the counts again.`,
      {
        context: {
          staleCascade: true,
          scope: plan.scope,
          drifted,
          confirmed: plan.counts,
          actual: fresh.counts,
        },
      },
    );
  }

  const { ids } = fresh;
  // Leaves first, roots last: if any step throws, the transaction aborts and nothing is committed —
  // but ordering it this way keeps the intermediate state inside the transaction sane for anything
  // (a postcondition, a future assertion) that reads mid-cascade.
  await deleteObservationsForCascade(ids.observations);
  await deleteResolutionsForCascade(ids.flagResolutions);
  await deleteTalkPointsForCascade(ids.talkPoints);
  await deleteNotesForCascade(ids.matchNotes);
  await deleteAnnotationsForCascade(ids.annotations);
  await deleteCoachCardsForCascade(ids.coachCards);
  await deleteSetPiecesForCascade(ids.setPieces);
  await deleteHypothesesForCascade(ids.hypotheses);
  // DD-2's dividend: audio is in the same IndexedDB, so the blobs go in this transaction too.
  await deleteBlobs(ids.audioBlobs);
  await deleteMatchesForCascade(ids.matches);
  await deletePlayersForCascade(ids.players);
  await deleteTeamsForCascade(ids.teams);
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Collects every id in scope. Runs identically inside a readonly plan and inside the write
 * transaction — one function, so the confirm and the delete cannot disagree about what "dependent"
 * means.
 */
async function collectPlan(scope: CascadeScope): Promise<CascadePlan> {
  const handle = db();
  const ids: CascadeIds = {
    teams: [],
    players: [],
    matches: [],
    hypotheses: [],
    observations: [],
    flagResolutions: [],
    setPieces: [],
    talkPoints: [],
    matchNotes: [],
    annotations: [],
    coachCards: [],
    audioBlobs: [],
  };

  let matchIds: string[];
  if (scope.kind === 'team') {
    const team = await handle.teams.get(scope.id);
    if (!team) {
      throw new StorageError(`Team ${scope.id} no longer exists; there is nothing to delete.`, {
        context: { store: TABLE.teams, id: scope.id },
      });
    }
    ids.teams = [team.id];
    ids.players = (await handle.players.where('teamId').equals(team.id).toArray()).map((r) => r.id);
    matchIds = (await handle.matches.where('teamId').equals(team.id).toArray()).map((r) => r.id);
  } else {
    const match = await handle.matches.get(scope.id);
    if (!match) {
      throw new StorageError(`Match ${scope.id} no longer exists; there is nothing to delete.`, {
        context: { store: TABLE.matches, id: scope.id },
      });
    }
    matchIds = [match.id];
  }
  ids.matches = matchIds;

  const blobIds = new Set<string>();
  for (const matchId of matchIds) {
    const observations = await handle.observations.where('matchId').equals(matchId).toArray();
    for (const row of observations) {
      ids.observations.push(row.id);
      if (row.audioBlobId !== null) blobIds.add(row.audioBlobId);
    }

    const notes = await handle.matchNotes.where('matchId').equals(matchId).toArray();
    for (const row of notes) {
      ids.matchNotes.push(row.id);
      if (row.audioBlobId !== null) blobIds.add(row.audioBlobId);
    }

    ids.hypotheses.push(
      ...(await handle.hypotheses.where('matchId').equals(matchId).toArray()).map((r) => r.id),
    );
    ids.setPieces.push(
      ...(await handle.setPieces.where('matchId').equals(matchId).toArray()).map((r) => r.id),
    );
    ids.talkPoints.push(
      ...(await handle.talkPoints.where('matchId').equals(matchId).toArray()).map((r) => r.id),
    );
    // B-1: both new match-scoped tables, by the same `matchId` reach as their siblings above.
    ids.annotations.push(
      ...(await handle.annotations.where('matchId').equals(matchId).toArray()).map((r) => r.id),
    );
    ids.coachCards.push(
      ...(await handle.coachCards.where('matchId').equals(matchId).toArray()).map((r) => r.id),
    );
  }

  // Revision 2 added FlagResolution to what cascades. Reached through the flag Observations, because
  // the row deliberately carries no `matchId` of its own (§3.1) — one fact, one place.
  if (ids.observations.length > 0) {
    ids.flagResolutions = (
      await handle.flagResolutions.where('observationId').anyOf(ids.observations).toArray()
    ).map((r) => r.id);
  }

  ids.audioBlobs = [...blobIds];

  // Sorted so the plan is order-independent: two plans of the same data compare equal regardless of
  // the order IndexedDB happened to return rows in.
  for (const key of Object.keys(ids) as (keyof CascadeIds)[]) ids[key].sort();

  const counts = Object.fromEntries(
    (Object.keys(ids) as (keyof CascadeIds)[]).map((key) => [key, ids[key].length]),
  ) as Record<keyof CascadeIds, number>;

  return {
    scope,
    ids,
    counts,
    total: Object.values(counts).reduce((sum, n) => sum + n, 0),
  };
}

/** Which tables the confirmed plan and the current database disagree about. */
function driftedTables(confirmed: CascadeIds, actual: CascadeIds): (keyof CascadeIds)[] {
  return (Object.keys(confirmed) as (keyof CascadeIds)[]).filter((key) => {
    const a = confirmed[key];
    const b = actual[key];
    return a.length !== b.length || a.some((id, i) => id !== b[i]);
  });
}
